/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { getData, postData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import { assetImages } from "constants";

const CustomerPricingUpload = ({
  productList = [],
  onSuccess = () => {},
  onClose = () => {},
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const history = useHistory(); // useHistory instead of useNavigate

  // Options & selections
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companies, setCompanies] = useState([]); // array of {value,label}

  const [currentStep, setCurrentStep] = useState("1");

  // generated combinations
  const [combinations, setCombinations] = useState([]); // each { id, product, company, price, quantity, startDate, endDate }

  // UI / feedback
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const [showSuccessActions, setShowSuccessActions] = useState(false); // New state

  // helper: fetch companies (same API usage as ProductFilterPopup)
  const fetchCompanies = async () => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_COMPANY +
        `?token=${token}&modulefrom=backend`;
      const resp = await getData(requestUrl);
      console.log("company options", resp.data);
      if (resp.status && Array.isArray(resp.data)) {
        setCompanyOptions(resp.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // debounce minimal: only fetch when user typed 2+ chars
  useEffect(() => {
    fetchCompanies();
    setCurrentStep("1");
  }, []);

  // Generate all combinations of selected products and companies
  const generatePricing = () => {
    if (!productList.length || !companies.length) {
      setNotification(t("Please select at least one product and one company."));
      setTimeout(() => setNotification(""), 3000);
      return;
    }

    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const todayStr = `${yyyy}-${mm}-${dd}`;

    // Build combinations: each product-company pair
    const combos = [];
    productList.forEach((prod) => {
      companies.forEach((comp) => {
        combos.push({
          id: `${prod._id}__${comp._id}`,
          product: { ...prod, label: prod.name, value: prod._id },
          company: { ...comp, label: comp.name, value: comp._id },
          price: "",
          quantity: 1,
          startDate: todayStr,
          endDate: todayStr,
        });
      });
    });

    setCombinations(combos);
  };

  // update a combination field
  const updateCombination = (id, field, value) => {
    setCombinations((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              [field]:
                field === "quantity" || field === "price"
                  ? value.replace(/[^0-9.]/g, "") // allow only numbers and dot
                  : value,
            }
          : c
      )
    );
  };

  // Remove a combination
  const removeCombination = (id) => {
    setCombinations((prev) => prev.filter((c) => c.id !== id));
  };

  // CSV / Excel download helper (creates a CSV compatible with Excel)
  const downloadCsv = () => {
    // Use current combinations if available, else build template from selected products/companies
    let rowsSource = [];
    if (combinations.length > 0) {
      rowsSource = combinations;
    } else if (productList.length && companies.length) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const todayStr = `${yyyy}-${mm}-${dd}`;
      productList.forEach((prod) => {
        companies.forEach((comp) => {
          rowsSource.push({
            id: `${prod._id}__${comp._id}`,
            product: { ...prod, label: prod.name, value: prod._id },
            company: { ...comp, label: comp.name, value: comp._id },
            price: "",
            quantity: 1,
            startDate: todayStr,
            endDate: todayStr,
          });
        });
      });
    }

    const headers = [
      "productName",
      "productId",
      "companyName",
      "companyId",
      "price",
      "minQuantity",
      "startDate",
      "endDate",
    ];

    const dataRows = rowsSource.map((r) => ({
      productName: r.product?.label ?? "",
      productId: r.product?.value ?? "",
      companyName: r.company?.label ?? "",
      companyId: r.company?.value ?? "",
      price: r.price ?? "",
      minQuantity:
        r.quantity !== undefined && r.quantity !== "" ? r.quantity : 1,
      startDate: r.startDate ?? "",
      endDate: r.endDate ?? "",
    }));

    // Convert to CSV (escape double quotes)
    const csvContent =
      "\uFEFF" +
      [headers.join(",")]
        .concat(
          dataRows.map((row) =>
            headers
              .map((h) => {
                const cell = row[h] ?? "";
                const escaped = `${cell}`.replace(/"/g, '""');
                return `"${escaped}"`;
              })
              .join(",")
          )
        )
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const urlBlob = URL.createObjectURL(blob);
    link.href = urlBlob;
    const fileName =
      combinations.length > 0
        ? "customer_pricing_rows.csv"
        : productList.length && companies.length
        ? "customer_pricing_template.csv"
        : "customer_pricing_template_empty.csv";
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(urlBlob);
  };

  // Validator for combinations
  const validateCombinations = (combinations) => {
    for (const c of combinations) {
      // Price must not be empty
      if (c.price === "" || isNaN(Number(c.price))) {
        return {
          valid: false,
          message: t("Please provide a valid price for all rows."),
        };
      }
      // startDate and endDate must not be empty
      if (!c.startDate || !c.endDate) {
        return {
          valid: false,
          message: t("Please provide both start and end dates for all rows."),
        };
      }
      // startDate must be <= endDate
      if (new Date(c.startDate) > new Date(c.endDate)) {
        return {
          valid: false,
          message: t(
            "Start date must be before or equal to end date for all rows."
          ),
        };
      }
    }
    return { valid: true };
  };

  // Submit payload to API
  const handleSubmit = async () => {
    setShowValidation(true); // Show validation errors on submit
    if (!combinations.length) {
      setNotification(t("No pricing rows to submit."));
      setTimeout(() => setNotification(""), 1500);
      return;
    }

    // Use validator
    const validation = validateCombinations(combinations);
    if (!validation.valid) {
      setNotification(validation.message);
      setTimeout(() => setNotification(""), 2500);
      return;
    }

    // Basic validation: ensure min quantity
    for (const c of combinations) {
      if (
        c.quantity === "" ||
        isNaN(Number(c.quantity)) ||
        Number(c.quantity) < 1
      ) {
        setNotification(
          t("Please provide a valid minimum quantity (>=1) for all rows.")
        );
        setTimeout(() => setNotification(""), 2500);
        return;
      }
    }

    // Build payload
    const payload = combinations.map((c) => ({
      productid: c.product._id,
      companyid: c.company._id,
      price: Number(c.price),
      minquantity: c.quantity ? Number(c.quantity) : 1,
      startdate: c.startDate ? new Date(c.startDate) : null,
      enddate: c.endDate ? new Date(c.endDate) : null,
    }));

    console.log("Payload:", payload);

    try {
      setSubmitting(true);
      const endpoint =
        url.API_BASE_URL +
        url.API_BULK_CREATE_PRODUCT_CUSTOMER_PRICING +
        `?token=${token}`;

      const response = await postData(endpoint, { pricings: payload });

      console.log("Response:", response);

      setSubmitting(false);
      if (response.status) {
        setNotification(t("Pricing uploaded successfully."));
        setTimeout(() => setNotification(""), 2500);
        setShowSuccessActions(true); // Show success actions

        // Hide offcanvas after a delay if needed, or keep open for user action
        // Comment out the following lines to keep offcanvas open for new buttons
        // const myOffcanvas = document.getElementById("offcanvasCustomerPricing");
        // const offcanvas = bootstrap.Offcanvas.getInstance(myOffcanvas);
        // offcanvas.hide();
      } else {
        setNotification(response.message || t("Upload failed."));
        setTimeout(() => setNotification(""), 3000);
      }
    } catch (err) {
      setSubmitting(false);
      console.error(err);
      setNotification(err.message || t("Upload failed."));
      setTimeout(() => setNotification(""), 3000);
    }
  };

  const resetAll = () => {
    setCompanies([]);
    setCombinations([]);
    setShowValidation(false); // Reset validation state
    setNotification("");
    setCurrentStep("1");
    setShowSuccessActions(false); // Reset success actions
  };

  // Handler for Close button
  const handleClose = () => {
    resetAll();
    onClose();
    // Hide offcanvas
    const myOffcanvas = document.getElementById("offcanvasCustomerPricing");
    if (myOffcanvas) {
      const offcanvas = window.bootstrap
        ? window.bootstrap.Offcanvas.getInstance(myOffcanvas)
        : bootstrap.Offcanvas.getInstance(myOffcanvas);
      if (offcanvas) offcanvas.hide();
    }
  };

  // Handler for Customer Price button
  const handleGoToCustomerPricing = () => {
    resetAll();
    // Hide offcanvas
    const myOffcanvas = document.getElementById("offcanvasCustomerPricing");
    if (myOffcanvas) {
      const offcanvas = window.bootstrap
        ? window.bootstrap.Offcanvas.getInstance(myOffcanvas)
        : bootstrap.Offcanvas.getInstance(myOffcanvas);
      if (offcanvas) offcanvas.hide();
    }
    // Navigate to customer pricing page (adjust path as needed)
    history.push("/admin/marketplace/product/customerpricing");
  };

  // Get validation errors for a row
  const getRowValidation = (row) => {
    const errors = {};
    // Price validation
    if (row.price === "" || isNaN(Number(row.price))) {
      errors.price = true;
    }
    // startDate and endDate validation
    if (!row.startDate) {
      errors.startDate = true;
    }
    if (!row.endDate) {
      errors.endDate = true;
    }
    if (
      row.startDate &&
      row.endDate &&
      new Date(row.startDate) > new Date(row.endDate)
    ) {
      errors.startDate = true;
      errors.endDate = true;
    }
    // Quantity validation
    if (
      row.quantity === "" ||
      isNaN(Number(row.quantity)) ||
      Number(row.quantity) < 1
    ) {
      errors.quantity = true;
    }
    return errors;
  };

  // Handle company checkbox selection
  const handleCompanyCheckbox = (company, checked) => {
    setCompanies((prev) => {
      if (checked) {
        // Add if not already present
        if (!prev.some((c) => c._id === company._id)) {
          return [...prev, { _id: company._id, name: company.name }];
        }
        return prev;
      } else {
        // Remove
        return prev.filter((c) => c._id !== company._id);
      }
    });
  };

  // Check if at least one company is selected
  const checkCompanySelected = () => {
    if (!companies || companies.length === 0) {
      setNotification(t("Please select at least one company."));
      setTimeout(() => setNotification(""), 2500);
      return false;
    }
    return true;
  };

  const nextStepHandler = () => {
    if (checkCompanySelected()) {
      setCurrentStep("2");
      generatePricing();
    }
  };

  const previousStepHandler = () => {
    setCurrentStep("1");
    setCombinations([]);
  };

  return (
    <div
      className="offcanvas offcanvas-end bg-white"
      tabIndex="-1"
      id="offcanvasCustomerPricing"
      aria-labelledby="offcanvasCustomerPricingLabel"
      style={{ width: "90vw", maxWidth: "1400px" }} // extra-large
    >
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              upload_file
            </span>{" "}
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasCustomerPricingLabel"
            >
              {t("Customer Pricing Upload")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {t("Upload and manage customer-specific product pricing")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={() => {
            resetAll();
            onClose();
          }}
        ></button>
      </div>

      <div className="offcanvas-body p-4">
        <div className={`${currentStep === "1" ? "" : "d-none"}`}>
          {/* --- Selected Products Table --- */}
          {productList && productList.length > 0 && (
            <div className="p-3 table-wrapper border border-gray-300 rounded-10 shadow-sm">
              <h5 className="fw-semibold">
                Step 1 : Selected Products{" "}
                <span className="material-symbols-outlined icon-sm text-success">
                  verified
                </span>
              </h5>
              <div className="table-responsive" style={{ maxHeight: "30vh" }}>
                <table className="table align-middle fs-sm text-nowrap border-0 mb-0">
                  <thead>
                    <tr>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Image")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("SKU")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Product Name")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Seller")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Price")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Stock")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Type")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Status")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {productList.map((product) => (
                      <tr key={product._id}>
                        <td className="border-bottom-0 p-1">
                          <img
                            src={url.SERVER_URL + product.productimagepath}
                            alt={product.name}
                            style={{
                              width: 50,
                              height: 50,
                              borderRadius: "8px",
                              objectFit: "cover",
                            }}
                          />
                        </td>
                        <td className="border-bottom-0 p-1">{product.sku}</td>
                        <td className="border-bottom-0 p-1">{product.name}</td>
                        <td className="border-bottom-0 p-1">
                          {product.companyname}
                        </td>
                        <td className="border-bottom-0 p-1">{product.price}</td>
                        <td className="border-bottom-0 p-1">{product.stock}</td>
                        <td className="border-bottom-0 p-1">
                          {product.producttypename}
                        </td>
                        <td className="border-bottom-0 p-1">
                          {product.productstatuslabel}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* --- Companies Table with Checkbox --- */}
          {companyOptions && companyOptions.length > 0 && (
            <div className="p-3 table-wrapper border border-gray-300 rounded-10 shadow-sm mt-4">
              <h5 className="fw-semibold">Step 2 : Select Companies</h5>{" "}
              <div className="table-responsive" style={{ maxHeight: "30vh" }}>
                <table className="table align-middle fs-sm text-nowrap border-0 mb-0">
                  <thead>
                    <tr>
                      <th className="bg-transparent fs-md fw-semibold border-0"></th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Logo")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Name")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Email")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("Location")}
                      </th>
                      <th className="bg-transparent fs-md fw-semibold border-0">
                        {t("City")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyOptions.map((company) => (
                      <tr key={company._id}>
                        <td className="border-bottom-0 p-1">
                          <input
                            type="checkbox"
                            checked={
                              !!companies.find((c) => c._id === company._id)
                            }
                            onChange={(e) =>
                              handleCompanyCheckbox(company, e.target.checked)
                            }
                          />
                        </td>
                        <td className="border-bottom-0 p-1">
                          <img
                            src={
                              company.companylogo !== ""
                                ? url.SERVER_URL + company.companylogo
                                : assetImages.defaultUser
                            }
                            alt={company.name}
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        </td>
                        <td className="border-bottom-0 p-1">{company.name}</td>
                        <td className="border-bottom-0 p-1">{company.email}</td>
                        <td className="border-bottom-0 p-1">
                          {company.location}
                        </td>
                        <td className="border-bottom-0 p-1">{company.city}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-3 my-4">
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={nextStepHandler}
            >
              {t("Next Step")}
              <span className="material-symbols-outlined me-1">
                arrow_forward
              </span>
            </button>
          </div>
        </div>

        <div
          className={`${
            currentStep === "2"
              ? "p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light"
              : "d-none"
          }`}
          style={{ height: "calc(100vh - 150px)", overflow: "auto" }}
        >
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Generated Combinations */}
            {combinations.length > 0 && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="mb-0 fw-semibold">
                    {t("Generated Pricing Rows")}
                  </h3>
                  <div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm me-2"
                      onClick={() => {
                        setCombinations([]);
                      }}
                    >
                      {t("Clear Rows")}
                    </button>

                    {/* Download CSV / Excel-compatible button */}
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm"
                      onClick={downloadCsv}
                      title={t("Download rows as Excel/CSV")}
                    >
                      {t("Download Excel")}
                    </button>
                  </div>
                </div>

                <div className="table-wrapper">
                  <div
                    className="table-responsive mb-1"
                    style={{
                      maxHeight: "calc(100vh - 50vh)",
                      overflow: "auto",
                    }}
                  >
                    <table className="table align-middle fs-sm text-nowrap border-0 mb-0">
                      <thead>
                        <tr>
                          <th className="bg-transparent fs-md fw-semibold border-0">
                            {t("Product")}
                          </th>
                          <th className="bg-transparent fs-md fw-semibold border-0">
                            {t("Company")}
                          </th>
                          <th
                            className="bg-transparent fs-md fw-semibold border-0"
                            style={{ minWidth: 140 }}
                          >
                            {t("Price")}
                          </th>
                          <th
                            className="bg-transparent fs-md fw-semibold border-0"
                            style={{ minWidth: 140 }}
                          >
                            {t("Min. Quantity")}
                          </th>
                          <th
                            className="bg-transparent fs-md fw-semibold border-0"
                            style={{ minWidth: 160 }}
                          >
                            {t("Start Date")}
                          </th>
                          <th
                            className="bg-transparent fs-md fw-semibold border-0"
                            style={{ minWidth: 160 }}
                          >
                            {t("End Date")}
                          </th>
                          <th className="bg-transparent fs-md fw-semibold border-0">
                            {t("Actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {combinations.map((row) => {
                          // Only show validation errors if showValidation is true (i.e., after submit)
                          const errors = showValidation
                            ? getRowValidation(row)
                            : {};
                          return (
                            <tr key={row.id}>
                              <td className="border-bottom-0 p-1">
                                {row.product.label}
                              </td>
                              <td className="border-bottom-0 p-1">
                                {row.company.label}
                              </td>
                              <td className="border-bottom-0 p-1">
                                <input
                                  type="number"
                                  min="0"
                                  className={`form-control form-control-sm shadow-none ${
                                    errors.price
                                      ? "border-primary"
                                      : "border-gray-300"
                                  }`}
                                  value={row.price}
                                  onChange={(e) =>
                                    updateCombination(
                                      row.id,
                                      "price",
                                      e.target.value
                                    )
                                  }
                                  placeholder="0.00"
                                />
                              </td>
                              <td className="border-bottom-0 p-1">
                                <input
                                  type="number"
                                  min="1"
                                  className={`form-control form-control-sm shadow-none ${
                                    errors.quantity
                                      ? "border-primary"
                                      : "border-gray-300"
                                  }`}
                                  value={row.quantity}
                                  onChange={(e) =>
                                    updateCombination(
                                      row.id,
                                      "quantity",
                                      e.target.value
                                    )
                                  }
                                  placeholder="1"
                                />
                              </td>
                              <td className="border-bottom-0 p-1">
                                <input
                                  type="date"
                                  className={`form-control form-control-sm shadow-none ${
                                    errors.startDate
                                      ? "border-primary"
                                      : "border-gray-300"
                                  }`}
                                  value={row.startDate}
                                  onChange={(e) =>
                                    updateCombination(
                                      row.id,
                                      "startDate",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              <td className="border-bottom-0 p-1">
                                <input
                                  type="date"
                                  className={`form-control form-control-sm shadow-none ${
                                    errors.endDate
                                      ? "border-primary"
                                      : "border-gray-300"
                                  }`}
                                  value={row.endDate}
                                  onChange={(e) =>
                                    updateCombination(
                                      row.id,
                                      "endDate",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              <td className="border-bottom-0 p-1">
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => removeCombination(row.id)}
                                >
                                  {t("Remove")}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Submit / Reset */}
                <div className="action d-flex justify-content-between pt-3 border-top mt-auto">
                  {!showSuccessActions ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-outline-primary d-flex align-items-center gap-2 me-2"
                        onClick={previousStepHandler}
                        disabled={submitting}
                      >
                        <span className="material-symbols-outlined me-1">
                          arrow_back
                        </span>
                        {t("Back")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center gap-2"
                        onClick={handleSubmit}
                        disabled={submitting}
                        style={{
                          cursor: submitting ? "not-allowed" : "pointer",
                        }}
                      >
                        {submitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            {t("Submitting...")}
                          </>
                        ) : (
                          <>
                            <span className="material-symbols-outlined me-1">
                              save
                            </span>{" "}
                            {t("Submit Pricing")}
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-outline-secondary d-flex align-items-center gap-2 me-2"
                        onClick={handleClose}
                      >
                        <span className="material-symbols-outlined me-1">
                          close
                        </span>
                        {t("Close")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-success d-flex align-items-center gap-2"
                        onClick={handleGoToCustomerPricing}
                      >
                        <span className="material-symbols-outlined me-1">
                          list_alt
                        </span>
                        {t("Customer Price")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </form>
        </div>

        {/* --- Notification and Submit Button --- */}
        {notification && (
          <div className="alert alert-info alert-dismissible fade show mt-3">
            {notification}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerPricingUpload;
